import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getPlanetAbbr } from '../../i18n/astrology';
import { isOuterPlanetKey, useUserPreferences } from '../../contexts/UserPreferencesContext';

type LalKitabChartProps = {
  chartData: any;
  size?: number;
  title?: string;
  showHouseLabels?: boolean;
  showPlanetTable?: boolean;
  isHindi?: boolean;
};

const CHART_SIZE = 400;

const HOUSE_POSITIONS = [
  { id: 1, x: 50, y: 20 },
  { id: 2, x: 25, y: 5 },
  { id: 3, x: 5, y: 20 },
  { id: 4, x: 20, y: 50 },
  { id: 5, x: 5, y: 80 },
  { id: 6, x: 25, y: 95 },
  { id: 7, x: 50, y: 80 },
  { id: 8, x: 75, y: 95 },
  { id: 9, x: 95, y: 80 },
  { id: 10, x: 80, y: 50 },
  { id: 11, x: 95, y: 20 },
  { id: 12, x: 75, y: 5 },
];

const SIGN_POSITIONS: Record<number, { x: number; y: number }> = {
  1: { x: 50, y: 25 },
  2: { x: 36, y: 10 },
  3: { x: 12, y: 27 },
  4: { x: 34, y: 48 },
  5: { x: 12, y: 73 },
  6: { x: 36, y: 90 },
  7: { x: 50, y: 73 },
  8: { x: 64, y: 90 },
  9: { x: 88, y: 73 },
  10: { x: 66, y: 48 },
  11: { x: 88, y: 27 },
  12: { x: 64, y: 10 },
};

const PLANET_ANCHORS: Record<number, { x: number; y: number; direction: 1 | -1 }> = {
  1: { x: 50, y: 13, direction: 1 },
  2: { x: 25, y: 14, direction: 1 },
  3: { x: 8, y: 35, direction: 1 },
  4: { x: 24, y: 46, direction: 1 },
  5: { x: 8, y: 70, direction: -1 },
  6: { x: 25, y: 84, direction: -1 },
  7: { x: 50, y: 74, direction: -1 },
  8: { x: 75, y: 84, direction: -1 },
  9: { x: 90, y: 70, direction: -1 },
  10: { x: 76, y: 46, direction: 1 },
  11: { x: 92, y: 35, direction: 1 },
  12: { x: 75, y: 14, direction: 1 },
};

const PLANET_NAME_MAP: Record<string, string> = {
  SUN: 'Sun',
  MOON: 'Moon',
  MARS: 'Mars',
  MERCURY: 'Mercury',
  JUPITER: 'Jupiter',
  VENUS: 'Venus',
  SATURN: 'Saturn',
  RAHU: 'Rahu',
  KETU: 'Ketu',
};

const PLANET_HI: Record<string, string> = {
  sun: 'सूर्य',
  moon: 'चन्द्रमा',
  mars: 'मंगल',
  mercury: 'बुध',
  jupiter: 'बृहस्पति',
  venus: 'शुक्र',
  saturn: 'शनि',
  rahu: 'राहु',
  ketu: 'केतु',
};

const PLANET_ORDER = ['sun', 'moon', 'mars', 'mercury', 'jupiter', 'venus', 'saturn', 'rahu', 'ketu'];

const isVarshChart = (chartData: any) => chartData?.type === 'LAL_KITAB_VARSHFAL';

const normalizePlanetName = (key: string, planet: any) => {
  const raw = String(planet?.name || key || '').trim();
  return PLANET_NAME_MAP[raw.toUpperCase()] || raw;
};

const normalizeSignNumber = (value: any) => {
  const sign = Number(value);
  return sign >= 1 && sign <= 12 ? sign : null;
};

const getAscSignNumber = (chartData: any) => (
  normalizeSignNumber(chartData?.ascSignNumber || chartData?.houses?.ascendant?.sign?.number || chartData?.ascendant?.sign?.number) || 1
);

const getDisplayHouseFromSign = (signNumber: number, ascSign: number) => (
  ((signNumber - ascSign + 12) % 12) + 1
);

const getBirthLalKitabDisplayHouse = (planet: any, ascSign: number) => {
  const explicitLalKitabHouse = Number(planet?.lalKitabJanamHouse);
  if (explicitLalKitabHouse >= 1 && explicitLalKitabHouse <= 12) {
    return explicitLalKitabHouse;
  }

  const signNumber = Number(planet?.sign?.number || planet?.signNumber);
  if (signNumber >= 1 && signNumber <= 12) {
    return getDisplayHouseFromSign(signNumber, ascSign);
  }

  const d1House = Number(planet?.rashiHouse ?? planet?.d1House);
  if (d1House >= 1 && d1House <= 12) {
    return d1House;
  }

  const explicitHouse = Number(planet?.bhaav ?? planet?.bhav ?? planet?.lalKitabHouse ?? planet?.house ?? planet?.houseNumber);
  if (explicitHouse >= 1 && explicitHouse <= 12) {
    return explicitHouse;
  }

  return null;
};

const getPlanetHouseEntries = (chartData: any, showOuterPlanets: boolean) => {
  const ascSign = getAscSignNumber(chartData);

  if (isVarshChart(chartData) && Array.isArray(chartData?.varshphalRows)) {
    const entries: { planet: string; house: number | null }[] = chartData.varshphalRows
      .filter((row: any) => showOuterPlanets || !isOuterPlanetKey(row.planet || row.label || row.key))
      .map((row: any) => {
      const house = Number(row.varshphalHouse);
      return {
        planet: String(row.planet || row.label || row.key || ''),
        house: house >= 1 && house <= 12 ? house : null,
      };
    });

    return entries.filter((entry): entry is { planet: string; house: number } => (
      Boolean(entry.planet) && entry.house !== null && entry.house >= 1 && entry.house <= 12
    ));
  }

  if (isVarshChart(chartData) && chartData?.planetHouses) {
    return Object.entries(chartData.planetHouses)
      .filter(([planet]) => showOuterPlanets || !isOuterPlanetKey(planet))
      .map(([planet, house]) => {
        const lkHouse = Number(house);
        return {
          planet,
          house: lkHouse >= 1 && lkHouse <= 12 ? lkHouse : null,
        };
      })
      .filter((entry): entry is { planet: string; house: number } => entry.house !== null && entry.house >= 1 && entry.house <= 12);
  }

  return Object.entries(chartData?.planets || {})
    .filter(([key, planet]: any) => showOuterPlanets || !isOuterPlanetKey(planet?.name || key))
    .map(([key, planet]: any) => {
      const lkHouse = getBirthLalKitabDisplayHouse(planet, ascSign);
      return {
        planet: normalizePlanetName(key, planet),
        house: lkHouse || null,
      };
    })
    .filter((entry) => entry.house && entry.house >= 1 && entry.house <= 12);
};

const groupByHouse = (entries: { planet: string; house: number | null }[]) => (
  entries.reduce<Record<number, string[]>>((acc, entry) => {
    if (!entry.house) return acc;
    if (!acc[entry.house]) acc[entry.house] = [];
    acc[entry.house].push(entry.planet);
    return acc;
  }, {})
);

const getPlanetY = (house: number, index: number, total: number) => {
  const anchor = PLANET_ANCHORS[house];
  const gap = total >= 4 ? 18 : 22;
  const start = anchor.direction === 1
    ? anchor.y
    : anchor.y - ((total - 1) * gap / CHART_SIZE) * 100;

  return ((start / 100) * CHART_SIZE) + index * gap;
};

const LalKitabChart: React.FC<LalKitabChartProps> = ({
  chartData,
  size = 380,
  title,
  showHouseLabels = true,
  showPlanetTable = false,
  isHindi = false,
}) => {
  const { t } = useTranslation();
  const { showOuterPlanets } = useUserPreferences();
  const planetsByHouse = groupByHouse(getPlanetHouseEntries(chartData, showOuterPlanets));
  const chartTitle = title || (isVarshChart(chartData) ? 'Lal Kitab Varshphal Chart • Fixed Khana Layout' : 'Lal Kitab Chart');

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="subtitle1">{chartTitle}</Typography>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${CHART_SIZE} ${CHART_SIZE}`}
        style={{ border: '2px solid #111', backgroundColor: '#fff' }}
      >
        <rect x="0" y="0" width={CHART_SIZE} height={CHART_SIZE} fill="#fff" />
        <g stroke="#8a8a8a" strokeWidth="1">
          <line x1="0" y1="0" x2="400" y2="400" />
          <line x1="400" y1="0" x2="0" y2="400" />
          <line x1="200" y1="0" x2="0" y2="200" />
          <line x1="0" y1="200" x2="200" y2="400" />
          <line x1="200" y1="400" x2="400" y2="200" />
          <line x1="400" y1="200" x2="200" y2="0" />
        </g>

        {HOUSE_POSITIONS.map((position) => {
          const signPosition = SIGN_POSITIONS[position.id];
          const planets = planetsByHouse[position.id] || [];
          const anchor = PLANET_ANCHORS[position.id];
          const planetX = (anchor.x / 100) * CHART_SIZE;

          return (
            <g key={position.id}>
              {showHouseLabels ? (
                <text
                  x={(position.x / 100) * CHART_SIZE}
                  y={(position.y / 100) * CHART_SIZE - 8}
                  fill="#1743ff"
                  fontSize="10"
                  textAnchor="middle"
                >
                  H{position.id}
                </text>
              ) : null}
              <text
                x={(signPosition.x / 100) * CHART_SIZE}
                y={(signPosition.y / 100) * CHART_SIZE}
                fill="#de9f33"
                fontSize="14"
                fontWeight="800"
                textAnchor="middle"
              >
                {position.id}
              </text>
              {planets.map((planet, index) => (
                <text
                  key={`${position.id}-${planet}-${index}`}
                  x={planetX}
                  y={getPlanetY(position.id, index, planets.length)}
                  fill="#1b5e20"
                  fontSize={planets.length >= 4 ? 15 : 17}
                  fontWeight="800"
                  textAnchor="middle"
                >
                  {getPlanetAbbr(t, planet)}
                </text>
              ))}
            </g>
          );
        })}
      </svg>
      {showPlanetTable && chartData?.planetHouses ? (
        <Table size="small" sx={{ mt: 2, maxWidth: size, mx: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>{isHindi ? 'ग्रह' : 'Planet'}</TableCell>
              {chartData?.janamPlanetHouses ? (
                <TableCell align="center" sx={{ fontWeight: 600 }}>{isHindi ? 'जन्म भाव' : 'Janam House'}</TableCell>
              ) : null}
              <TableCell align="center" sx={{ fontWeight: 600 }}>{isHindi ? 'वर्षफल भाव' : 'Varshphal House'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {PLANET_ORDER.filter((planet) => chartData.planetHouses[planet] !== undefined).map((planet) => (
              <TableRow key={planet}>
                <TableCell>{isHindi ? PLANET_HI[planet] || planet : planet.charAt(0).toUpperCase() + planet.slice(1)}</TableCell>
                {chartData?.janamPlanetHouses ? (
                  <TableCell align="center">{chartData.janamPlanetHouses[planet] || '--'}</TableCell>
                ) : null}
                <TableCell align="center">{chartData.planetHouses[planet]}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
    </Box>
  );
};

export default LalKitabChart;
